// PlanCard — one plan on the shelf, as a two-faced card with a quick-view.
//
// Three interactions, each with one job:
//
//  - the ⓘ control FLIPS the card. The front is for scanning — name, price,
//    chips — and the §25.475 price explainer (why a promotional plan shows no
//    single average) lives on the BACK, one tap away, instead of a paragraph
//    squatting on every card and making the shelf unreadable. The disclosure
//    is never dropped; it moved.
//  - "View plan" opens a QUICK-VIEW dialog: the same facts at reading size,
//    without leaving the shelf and losing your place in the grid.
//  - "Buy this plan", inside the quick-view, is the ONLY control that commits:
//    it navigates into the plan funnel (`/plan/:slug`), where the address,
//    credit and signing steps take over. Look twice, commit once.
//
// ⚠️ Still the PRE-METER marketing card: the headline is the §25.475 average
// at the CALLER's reference usage — never a raw energy charge that could be
// mistaken for "the price".

import type { PlanSummary } from '@rheora/enrollment-contract';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useTranslation } from '@fluxora/i18n';
import { Badge, Button, Card, CardBody, Checkbox, Dialog } from '@fluxora/ui';

import { usePlanDetailPrefetch } from '../api/plans.prefetch';
import { formatRenewablePct } from '../services/price.service';
import { tduDisplayName } from '../services/zone.service';
import { PlanPriceHeadline } from './PlanPriceHeadline';

export interface PlanCardProps {
  readonly plan: PlanSummary;
  /** The monthly kWh to headline at (a reference level on these pre-meter surfaces). */
  readonly monthlyKwh: number;
  /**
   * Compare wiring (E-SF11). Present ONLY on the shelf — when `onToggleCompare`
   * is supplied the card shows a "Compare" checkbox. The landing "featured"
   * cards omit these props, so no checkbox renders there.
   */
  readonly compareSelected?: boolean;
  readonly onToggleCompare?: () => void;
  /** True when the compare set is full and THIS card is not already in it. */
  readonly compareDisabled?: boolean;
  /**
   * The ZIP from the entry gate (E-SF10), carried onto the plan link so the
   * address step can prefill it and never re-ask. Absent on the landing's
   * featured cards (no gate there).
   */
  readonly zip?: string;
}

export function PlanCard({
  plan,
  monthlyKwh,
  compareSelected = false,
  onToggleCompare,
  compareDisabled = false,
  zip,
}: PlanCardProps): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [flipped, setFlipped] = useState(false);
  const [quickViewOpen, setQuickViewOpen] = useState(false);

  // Warm the plan detail on the signals that reliably precede a commit: the
  // quick-view opening, or the info flip (both mean "I'm considering this").
  const prefetchDetail = usePlanDetailPrefetch();

  const termLabel =
    plan.termMonths > 0 ? t('shop.card.term', { months: plan.termMonths }) : t('shop.card.term.none');
  const planUrl = `/plan/${encodeURIComponent(plan.tariffId)}${
    zip !== undefined ? `?zip=${encodeURIComponent(zip)}` : ''
  }`;

  const chips = (
    <div className="flex flex-wrap justify-center gap-2">
      <Badge variant="primary">{plan.fixedOrIndexed}</Badge>
      <Badge variant="default">{termLabel}</Badge>
      <Badge variant="success">
        {t('shop.card.renewable', { pct: formatRenewablePct(plan.recPctMin) })}
      </Badge>
    </div>
  );

  return (
    <div className={`flip ${flipped ? 'flipped' : ''}`}>
      <div className="flip-inner">
        {/* ── FRONT: scan me ─────────────────────────────────────────────── */}
        <Card
          variant="glass"
          padding="md"
          className="flip-face hover-lift relative flex h-full flex-col justify-between gap-4 shadow-card"
        >
          <CardBody className="flex flex-col gap-3 p-0">
            {/* The flip control, floated to the corner. A real button with a
                spoken name — the "ⓘ" glyph alone is a mystery to a screen
                reader. */}
            <button
              type="button"
              aria-label={t('shop.card.info.aria')}
              title={t('shop.card.info.aria')}
              onClick={() => {
                setFlipped(true);
                prefetchDetail(plan.tariffId);
              }}
              className="absolute right-4 top-4 flex h-7 w-7 items-center justify-center rounded-full border border-border text-xs font-bold text-foreground/60 transition-colors hover:border-primary hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              i
            </button>
            <div className="flex min-w-0 flex-col items-center gap-0.5 px-7 text-center">
              <span className="font-display w-full truncate text-xl font-bold tracking-tight text-foreground">
                {plan.planName}
              </span>
              <span className="text-sm text-foreground/60">{plan.supplierName}</span>
            </div>

            {/* Compact on the front — the withheld-price explainer is the back
                face's whole job. */}
            <PlanPriceHeadline planId={plan.tariffId} monthlyKwh={monthlyKwh} detail="compact" align="center" />

            {chips}

            {/* E-SF19 P2 — the utility's NAME, not its ERCOT code. */}
            <span className="text-xs text-foreground/60">
              {t('shop.card.tdsp', { tdsp: tduDisplayName(plan.tdsp) })}
            </span>

            {onToggleCompare !== undefined ? (
              <Checkbox
                checked={compareSelected}
                onChange={onToggleCompare}
                disabled={!compareSelected && compareDisabled}
                label={t('shop.card.compare')}
              />
            ) : null}
          </CardBody>

          <Button
            variant="primary"
            shape="pill"
            fullWidth
            className="cta-sheen"
            onMouseEnter={() => prefetchDetail(plan.tariffId)}
            onFocus={() => prefetchDetail(plan.tariffId)}
            onClick={() => setQuickViewOpen(true)}
          >
            {t('shop.card.viewDetail')}
          </Button>
        </Card>

        {/* ── BACK: read me ──────────────────────────────────────────────── */}
        <Card
          variant="glass"
          padding="md"
          className="flip-face flip-back flex h-full flex-col justify-between gap-4 shadow-card"
          aria-hidden={!flipped}
        >
          <CardBody className="flex flex-col gap-3 p-0">
            <span className="text-xs font-semibold uppercase tracking-wide text-foreground/50">
              {t('shop.card.aboutPrice')}
            </span>
            <span className="font-display text-lg font-bold tracking-tight text-foreground">
              {plan.planName}
            </span>
            {/* The FULL disclosure, including the withheld-price paragraph the
                front deliberately does not carry. */}
            <PlanPriceHeadline planId={plan.tariffId} monthlyKwh={monthlyKwh} detail="full" />
          </CardBody>
          <Button variant="secondary" shape="pill" fullWidth onClick={() => setFlipped(false)}>
            {t('shop.card.back')}
          </Button>
        </Card>
      </div>

      {/* ── QUICK-VIEW: decide here, commit from here ─────────────────────── */}
      {quickViewOpen ? (
        <Dialog
          open={quickViewOpen}
          onClose={() => setQuickViewOpen(false)}
          title={plan.planName}
          size="md"
          headerAlign="center"
          footer={
            <>
              <Button variant="secondary" onClick={() => setQuickViewOpen(false)}>
                {t('shop.card.quickview.close')}
              </Button>
              {/* The commit. From here the existing plan funnel owns the flow —
                  address, credit, documents, signature. */}
              <Button
                variant="primary"
                shape="pill"
                className="cta-sheen"
                onClick={() => navigate(planUrl)}
                data-testid={`buy-plan-${plan.tariffId}`}
              >
                {t('shop.card.quickview.buy')}
              </Button>
            </>
          }
        >
          <div className="flex flex-col gap-4">
            <span className="text-sm text-foreground/70">{plan.supplierName}</span>
            <PlanPriceHeadline planId={plan.tariffId} monthlyKwh={monthlyKwh} detail="full" />
            {chips}
            <span className="text-sm text-foreground/60">
              {t('shop.card.tdsp', { tdsp: tduDisplayName(plan.tdsp) })}
            </span>
            <p className="text-xs leading-relaxed text-foreground/50">
              {t('shop.card.quickview.docsNote')}
            </p>
          </div>
        </Dialog>
      ) : null}
    </div>
  );
}
